import { Alert, Button, Modal, Spin, Typography, message } from 'antd';
import { saveAs } from 'file-saver';
import { FC, useEffect, useMemo, useState } from 'react';

import { captureException } from '@sentry/react';

import { type TransactionBatch, useTransactionBatchRows } from '@hooks-api';
import { downloadCsv, toCsv } from '@utils/csv';

import { toIsoBookingDay } from './bookingDate';
import * as S from './styled';

type Props = {
  open: boolean;
  batch: TransactionBatch | null;
  companyId?: string;
  fileName: string;
  onClose: () => void;
};

const SENTRY_TAGS = { feature: 'transaction-batch-file' } as const;

const ROW_HEADERS = [
  'Booking date',
  'Amount',
  'Currency',
  'Details',
  'Counterparty',
];

const TransactionBatchFileModal: FC<Props> = ({
  open,
  batch,
  companyId,
  fileName,
  onClose,
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fileUrl = batch?.fileUrl ?? null;
  // A batch with no source file is rebuilt from the transactions it imported. Integration
  // batches never have a file: the bank sync creates them from the API response and uploads
  // nothing, so signing an S3 URL for them used to hand the viewer a guaranteed 404.
  const isRebuiltFromRows = open && batch !== null && fileUrl === null;

  const rowsQuery = useTransactionBatchRows(
    companyId,
    isRebuiltFromRows ? batch.id : null
  );

  const rebuiltCsv = useMemo(() => {
    if (!rowsQuery.data) {
      return null;
    }

    return toCsv(
      ROW_HEADERS,
      rowsQuery.data.map((row) => [
        toIsoBookingDay(row.bookingDate),
        row.amount,
        row.currencyCode,
        row.details ?? '',
        row.contactName ?? '',
      ])
    );
  }, [rowsQuery.data]);

  useEffect(() => {
    if (!open || !fileUrl) {
      return;
    }

    const controller = new AbortController();

    setIsLoading(true);
    setHasError(false);
    setContent(null);

    fetch(fileUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.status}`);
        }

        return response.text();
      })
      .then((text) => {
        setContent(text);
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }

        setHasError(true);
        captureException(error, { level: 'error', tags: SENTRY_TAGS });
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [open, fileUrl]);

  useEffect(() => {
    if (!rowsQuery.isError) {
      return;
    }

    captureException(rowsQuery.error, { level: 'error', tags: SENTRY_TAGS });
  }, [rowsQuery.isError, rowsQuery.error]);

  const handleDownload = async () => {
    // No source file: hand the browser the CSV we rebuilt from the rows.
    if (isRebuiltFromRows) {
      if (rebuiltCsv !== null) {
        downloadCsv(fileName, rebuiltCsv);
      }

      return;
    }

    if (!fileUrl) {
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      saveAs(await response.blob(), fileName);
    } catch (error) {
      message.error('Failed to download the file. Please try again.');
      captureException(error, { level: 'error', tags: SENTRY_TAGS });
    } finally {
      setIsDownloading(false);
    }
  };

  const isBusy = isRebuiltFromRows ? rowsQuery.isPending : isLoading;
  const isFailed = isRebuiltFromRows ? rowsQuery.isError : hasError;
  const shownContent = isRebuiltFromRows ? rebuiltCsv : content;
  const canDownload = isRebuiltFromRows ? rebuiltCsv !== null : Boolean(fileUrl);

  return (
    <Modal
      open={open}
      title={fileName || 'Source file'}
      width={900}
      centered
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="download"
          type="primary"
          loading={isDownloading}
          disabled={!canDownload}
          onClick={handleDownload}
        >
          Download
        </Button>,
      ]}
    >
      {isRebuiltFromRows && !isFailed && (
        <S.FileNotice
          type="info"
          showIcon
          message="This batch came from a bank integration, so there is no original file. The rows below are rebuilt from the transactions it imported."
        />
      )}

      {isBusy && (
        <S.FileModalStatus>
          <Spin />
          <Typography.Text type="secondary">
            {isRebuiltFromRows
              ? 'Rebuilding the batch from its transactions…'
              : 'Loading the source file…'}
          </Typography.Text>
        </S.FileModalStatus>
      )}

      {isFailed && (
        <Alert
          type="error"
          message={
            isRebuiltFromRows
              ? 'Could not load the transactions of this batch. Please try again.'
              : 'Could not load the file. Try downloading it instead.'
          }
        />
      )}

      {!isBusy && !isFailed && shownContent !== null && (
        <S.FileContent>{shownContent}</S.FileContent>
      )}
    </Modal>
  );
};

export default TransactionBatchFileModal;
